import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP_INSIGHTS_CONNECTION_STRING, CLIENT_TYPE_TOKEN } from '@raptor-fe/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
    private readonly connectionString = inject(APP_INSIGHTS_CONNECTION_STRING, { optional: true }) ?? '';
    private readonly clientType = inject(CLIENT_TYPE_TOKEN, { optional: true }) ?? '';
    private readonly router = inject(Router);

    private initialized = false;
    private appInsights: ApplicationInsights | null = null;

    /**
     * Initialises Application Insights with the configured connection string and Angular router
     * integration. Calling this method when no connection string is provided, or after it has
     * already been called, is a no-op.
     */
    initialize(): void {
        if (this.initialized || !this.connectionString) {
            return;
        }

        const angularPlugin = new AngularPlugin();

        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: this.connectionString,
                extensions: [angularPlugin],
                extensionConfig: {
                    [angularPlugin.identifier]: { router: this.router },
                },
            },
        });

        this.appInsights.loadAppInsights();

        if (this.clientType) {
            this.appInsights.addTelemetryInitializer((envelope) => {
                if (envelope.tags) {
                    envelope.tags['ai.cloud.role'] = this.clientType;
                }
            });
        }

        this.appInsights.trackPageView();
        this.initialized = true;
    }

    /**
     * Logs an exception to Application Insights. No-op if the service has not been initialised.
     */
    trackException(error: Error): void {
        if (!this.appInsights) {
            return;
        }

        this.appInsights.trackException({ exception: error });
    }

    /**
     * Logs a named event with optional property bag to Application Insights. No-op if the service
     * has not been initialised.
     */
    trackEvent(name: string, properties?: Record<string, string>): void {
        if (!this.appInsights) {
            return;
        }

        this.appInsights.trackEvent({ name }, properties);
    }

    /**
     * Logs a page view to Application Insights. No-op if the service has not been initialised.
     */
    trackPageView(name?: string, uri?: string): void {
        if (!this.appInsights) {
            return;
        }

        this.appInsights.trackPageView({ name, uri });
    }

    /**
     * Logs a trace message at the specified severity level to Application Insights. No-op if the
     * service has not been initialised.
     */
    trackTrace(message: string, severityLevel?: number): void {
        if (!this.appInsights) {
            return;
        }

        this.appInsights.trackTrace({ message, severityLevel });
    }
}
